import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Search, 
  Settings, 
  LogOut,
  Stethoscope,
  Menu,
  X,
  Building2,
  BarChart3,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NotificationBell from './NotificationBell';
import { useState } from 'react';
import { BACKEND_URL } from '../config/api';

const Layout = () => {
  const { user, logout } = useAuth();
  const { branding } = useNotifications();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const accentColor = branding?.primary_color || '#4B6651';
  const sidebarLogoUrl = branding?.logo_url ? (branding.logo_url.startsWith('http') ? branding.logo_url : `${BACKEND_URL}${branding.logo_url}`) : '';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/cases', icon: FolderOpen, label: 'Cases' },
    { to: '/edu-search', icon: Search, label: 'Edu Search' },
    { to: '/clinic', icon: Building2, label: 'Clinic' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/subscription', icon: CreditCard, label: 'Subscription' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex" data-testid="app-layout">
      {/* Mobile header bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-[#E5E4E0] flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-[#F8F7F4]"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <h1 className="text-base font-semibold text-[#2A2C2A] font-['Work_Sans']">
          {branding?.name || 'VetNeuroHelp'}
        </h1>
        <NotificationBell />
      </div>

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#E5E4E0]
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          pt-14 lg:pt-0
        `}
        data-testid="sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo / Clinic Branding */}
          <div className="p-6 border-b border-[#E5E4E0]">
            <div className="flex items-center gap-3">
              {sidebarLogoUrl ? (
                <img src={sidebarLogoUrl} alt="" className="w-10 h-10 object-contain rounded-lg" data-testid="sidebar-logo" />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans']">
                  {branding?.name || 'VetNeuroHelp'}
                </h1>
                <p className="text-xs text-[#5C5F5C]">Veterinary Neurology Assistant</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                  transition-all duration-200
                  ${isActive 
                    ? 'bg-[#4B6651] text-white' 
                    : 'text-[#5C5F5C] hover:bg-[#F8F7F4] hover:text-[#2A2C2A]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#E5E4E0]">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-8 h-8 bg-[#4B6651]/10 rounded-full flex items-center justify-center overflow-hidden">
                {user?.avatar_url ? (
                  <img src={user.avatar_url.startsWith('http') ? user.avatar_url : `${BACKEND_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-medium text-[#4B6651]">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2A2C2A] truncate">{user?.name}</p>
                <p className="text-xs text-[#5C5F5C] truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-[#5C5F5C] hover:text-[#D94F4F] hover:bg-[#D94F4F]/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 pt-4 md:px-8">
          <NotificationBell />
        </div>
        <div className="p-4 sm:p-6 md:p-8 lg:pl-8 pt-4 lg:pt-2">
          <Outlet />
        </div>
        <footer className="px-4 sm:px-6 md:px-8 lg:pl-8 pb-16 pt-4 border-t border-[#E5E4E0] mt-8" data-testid="app-footer">
          <div className="flex flex-wrap gap-4 text-xs text-[#5C5F5C]">
            <NavLink to="/terms" className="hover:text-[#4B6651] hover:underline">Terms of Service</NavLink>
            <NavLink to="/privacy" className="hover:text-[#4B6651] hover:underline">Privacy Policy</NavLink>
            <NavLink to="/contact" className="hover:text-[#4B6651] hover:underline">Contact Us</NavLink>
            <span className="ml-auto">VetNeuroHelp, Ellijay, GA</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
